import {
   isCalendarMaxGridInstructor,
   normalizeInstructorType,
} from "../../../constants/instructors";
import { ULTRA_LATERAL_INSTRUCTOR_ID } from "../../../calendar/interaction/constants";
import {
   normalizeColorToCss,
   resolveInstructorHeaderStyles,
   resolveSectorColor,
} from "./extremeEventColors";

export function parsePositiveInt(value) {
   const parsed = Number(value);
   if (!Number.isFinite(parsed)) return null;
   if (parsed <= 0) return null;
   return Math.trunc(parsed);
}

export function getInstructorDisplayName(inst) {
   const full = `${inst?.firstName ?? ""} ${inst?.lastName ?? ""}`.trim();
   if (full) return full;
   const fallback = String(inst?.name ?? "").trim();
   return fallback || "Instructor";
}

export function buildInstructorInitials(inst) {
   if (!inst) return "";
   let firstName = String(inst.firstName ?? "").trim();
   let lastName = String(inst.lastName ?? "").trim();
   if (!firstName || !lastName) {
      const parts = String(inst.name ?? "")
         .trim()
         .split(/\s+/)
         .filter(Boolean);
      if (!firstName && parts.length) firstName = parts[0];
      if (!lastName && parts.length > 1) lastName = parts[parts.length - 1];
   }
   if (!firstName && !lastName) return "";
   const firstPart = firstName.slice(0, 3);
   const lastPart = lastName.slice(0, 1);
   if (firstPart && lastPart) return `${firstPart}.${lastPart}`;
   return firstPart || lastPart;
}

export function normalizeSectorSlug(raw) {
   const value = String(raw || "").trim().toLowerCase();
   if (value.includes("botanica")) return "botanica";
   if (value.includes("ciocana")) return "ciocana";
   if (value.includes("bui")) return "buiucani";
   return "other";
}

export function sectorSlugToLabel(sectorSlug, fallback = "Botanica") {
   const normalized = normalizeSectorSlug(sectorSlug);
   if (normalized === "ciocana") return "Ciocana";
   if (normalized === "buiucani") return "Buiucani";
   if (normalized === "botanica") return "Botanica";
   // un sector nou (adăugat din Instrumente) rămâne cu numele lui: cine
   // cheamă dă numele real ca fallback ("" = lasă-l gol)
   return String(fallback ?? "").trim();
}

export function getInstructorSectorSlugById(
   instructorId,
   instructorObjectById,
   instructorSectorById,
) {
   const rawId = String(instructorId || "").trim();
   if (!rawId) return "other";

   const aliases = [rawId];
   const parsedId = parsePositiveInt(rawId);
   if (parsedId != null) {
      const parsedAlias = String(parsedId);
      if (!aliases.includes(parsedAlias)) aliases.push(parsedAlias);
   }

   for (const alias of aliases) {
      const fromObject = String(
         instructorObjectById?.get(alias)?.sectorSlug || "",
      ).trim();
      if (fromObject) return normalizeSectorSlug(fromObject);

      const fromGroup = String(instructorSectorById?.get(alias) || "").trim();
      if (fromGroup) return normalizeSectorSlug(fromGroup);
   }

   return "other";
}

export function hasSectorMismatchWithInstructor(reservationSector, instructorSectorSlug) {
   const reservationSlug = normalizeSectorSlug(reservationSector);
   const instructorSlug = normalizeSectorSlug(instructorSectorSlug);
   if (reservationSlug === "other") return false;
   if (instructorSlug === "other") return false;
   return reservationSlug !== instructorSlug;
}

export function normName(value) {
   return String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();
}

export function digitsOnly(value) {
   return String(value || "").replace(/\D+/g, "");
}

export function getInstructorIdText(inst) {
   return String(inst?.id ?? inst?.instructorId ?? "").trim();
}

export function normalizeInstructorTypeValue(inst) {
   return normalizeInstructorType(
      inst?.instructorType ?? inst?.instructor_type ?? inst?.type,
   );
}

export function shouldIncludeInstructorInCalendarMaxGrid(inst, scopedInstructorId = "") {
   return isCalendarMaxGridInstructor(inst, {
      scopedInstructorId,
      lateralInstructorId: ULTRA_LATERAL_INSTRUCTOR_ID,
   });
}

export function normalizeGearboxDisplayLabel(raw) {
   const value = String(raw || "").trim().toLowerCase();
   if (!value) return "";
   if (value.includes("auto")) return "Automat";
   if (value.includes("man")) return "Manual";
   return String(raw || "").trim();
}

export function resolveInstructorSectorLabel(inst, instructorSectorById) {
   const id = getInstructorIdText(inst);
   const slug = getInstructorSectorSlugById(id, null, instructorSectorById);
   const fromInst = normalizeSectorSlug(inst?.sector ?? inst?.sectorSlug ?? "");
   const resolved = slug !== "other" ? slug : fromInst;
   const label = sectorSlugToLabel(resolved, "");
   return label || String(inst?.sector ?? inst?.sectorSlug ?? "").trim();
}

export function resolveInstructorGearboxLabel(inst, cars) {
   const id = getInstructorIdText(inst);
   const carList = Array.isArray(cars) ? cars : [];
   let gearboxRaw = inst?.gearbox ?? inst?.transmission ?? "";
   if (!gearboxRaw && id) {
      for (const car of carList) {
         const carInstId = String(
            car?.instructorId ??
               car?.instructor_id ??
               car?.instructor ??
               "",
         ).trim();
         if (carInstId !== id) continue;
         gearboxRaw = car?.gearbox ?? car?.transmission ?? car?.gearboxType ?? "";
         break;
      }
   }
   return normalizeGearboxDisplayLabel(gearboxRaw);
}

export function buildInstructorContextSelectOption(inst, instructorSectorById, cars) {
   const id = getInstructorIdText(inst);
   if (!id) return null;
   const type = normalizeInstructorTypeValue(inst);
   const name = getInstructorDisplayName(inst);
   return {
      value: id,
      label: type === "SCHIMB" ? `${name} (schimb)` : name,
      type,
      sector: resolveInstructorSectorLabel(inst, instructorSectorById),
      gearbox: resolveInstructorGearboxLabel(inst, cars),
   };
}

export {
   normalizeColorToCss,
   resolveInstructorHeaderStyles,
   resolveSectorColor,
};
